import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { GrSnackbar, GrSnackbarProps } from "@jnpmedi/grebob";
import { NotificationId, NotificationPayload } from "./type";
import { uniqueId } from "lodash";

interface NotificationContextProps {
  notify: (payload: NotificationPayload) => NotificationId;
  onNotificationClose: (id?: NotificationId) => void;
}

export let enqueueSnackbar: (payload: NotificationPayload) => NotificationId | never;

export const NotificationContext = createContext<NotificationContextProps | null>(null);

const useNotificationContext = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error("useNotificationContext must be used within NotificationContextProvider");
  }

  return context;
};

function NotificationContextProvider({ children }: { children: ReactNode }) {
  const [snackbar, setSnackbar] = useState<NotificationPayload | undefined>();

  const notify = useCallback((payload: NotificationPayload): NotificationId => {
    const id = payload.id || uniqueId("notification-");

    setSnackbar({ ...payload, open: true, id });

    return id;
  }, []);

  const closeSnackbar = useCallback((id?: NotificationId) => {
    if (id) {
      setSnackbar((prev) => {
        if (prev?.id === id) {
          return { ...prev, open: false };
        }

        return prev;
      });
      return;
    }

    setSnackbar((prev) => (prev ? { ...prev, open: false } : prev));
  }, []);

  const handleClose = () => {
    if (snackbar?.type !== "action") {
      closeSnackbar(snackbar?.id);
    }
  };

  const getActionProps = (): GrSnackbarProps["action"] => {
    if (!snackbar || !snackbar.action) {
      return undefined;
    }

    const snackbarAction = snackbar.action;
    const isObj = typeof snackbarAction === "object";

    if (isObj && "text" in snackbarAction) {
      const { text, onClick } = snackbarAction;
      return {
        text,
        onClick: () => {
          onClick?.();
          closeSnackbar();
        },
      };
    }

    if (isObj && "icon" in snackbarAction && "onClick" in snackbarAction && typeof snackbarAction.onClick === "function") {
      const { icon, onClick } = snackbarAction;

      return {
        icon,
        onClick: () => {
          onClick?.();
          closeSnackbar();
        },
      };
    }

    if (isObj && "onClick" in snackbarAction && typeof snackbarAction.onClick === "function") {
      const { onClick } = snackbarAction;

      return {
        onClick: () => {
          onClick?.();
          closeSnackbar();
        },
      };
    }

    return snackbarAction;
  };

  const action = getActionProps();

  useEffect(() => {
    enqueueSnackbar = (payload) => {
      if (!notify) {
        throw Error("enqueSnacbar is not initialized");
      }

      return notify(payload);
    };
  }, [notify]);

  return (
    <NotificationContext.Provider value={{ notify, onNotificationClose: closeSnackbar }}>
      <StyledSnackbar {...snackbar} onClose={handleClose} action={action} />
      {children}
    </NotificationContext.Provider>
  );
}

export { NotificationContextProvider, useNotificationContext };

const StyledSnackbar = styled(GrSnackbar)({
  ".MuiPaper-root": {
    boxSizing: "content-box",
  },

  ".MuiAlert-message": {
    maxWidth: "100%",
    flex: 1,
    wordBreak: "break-word",
  },
});
